<template>
  <div v-rect-loading="loading" class="questionnaire-editor">
    <div class="header">
      <div class="title">{{ $t(`${i18nPrefix}.title.questionnaire.${isEditMode ? 'edit' : 'add'}`) }}</div>
    </div>
    <div class="content">
      <HeaderImageUploader
        ref="uploader"
        :pcImage="questionnaire.pc_image"
        :mobileImage="questionnaire.mobile_image"
        :acceptFileType="acceptFileType"
        :acceptFileSize="acceptFileSize"
        :rejectFileTypeMessage="$t('common.message.wrongImageType')"
        :rejectFileSizeMessage="$t('common.message.wrongImageSize', { size: 5 })"
        :tipMessage="{
          mobile: $t(`${i18nPrefix}.headerImage.tipMessage.mobile`),
          pc: $t(`${i18nPrefix}.headerImage.tipMessage.pc`)
        }"
        @file-change="handleFileChange"
      />
      <WrappedFrom
        ref="formEditor"
        :labelWidth="labelWidth"
        :model="questionnaire"
        :formLines="formLines"
        @cancel="handleCancel"
      >
        <template #steps="{ lineName }">
          <div class="steps-list-container">
            <div class="top-btn-group">
              <el-button class="add-btn" @click="openStepSelector">{{ $t('common.btn.add') }}</el-button>
              <el-button class="delete-btn" @click="removeQuestionnaireStep">{{ $t('common.btn.delete') }}</el-button>
            </div>
            <TableList
              noHead
              noAction
              ref="questionnaireSteps"
              :listData="questionnaire[lineName]"
              :tableColumns="formQuestionnaireTableColumns"
              @selection-change="handleFormQuestionnaireSelectionChange"
            >
              <template #draggable>
                <div class="drag-btn">
                  <i class="iconfont iconMore draggable-handle"></i>
                </div>
              </template>
              <template #icon="{ data }">
                <i class="step-icon" :class="data"></i>
              </template>
            </TableList>
          </div>
        </template>
      </WrappedFrom>
      <div class="bottom-btn-group">
        <el-button class="cancel-btn" @click="handleCancel">{{ $t('common.btn.cancel') }}</el-button>
        <el-button class="save-btn" @click="handleSave">{{ $t('common.btn.save') }}</el-button>
      </div>
    </div>
    <DialogWrapper
      class="step-selector"
      :loading="stepSelectorLoading"
      :show.sync="showStepSelector"
      :title="$t(`${i18nPrefix}.title.stepSelector`)"
      @close="closeStepSelector"
      @save="handleStepSelect"
    >
      <div class="filter-container">
        <el-input
          v-model="stepSearchCondition.keyword"
          class="keyword"
          suffix-icon="el-icon-search"
          :placeholder="$t(`${i18nPrefix}.placeholder.searchKeyword.step`)"
          @change="fetchStepListData"
          @keypress.enter.native="fetchStepListData"
        ></el-input>
      </div>
      <TableList
        ref="stepList"
        noHead
        noAction
        :listData="stepList"
        :tableColumns="questionnaireTableColumns"
        @selection-change="handleQuestionnaireSelectionChange"
      >
        <template #icon="{ data }">
          <i class="step-icon" :class="data"></i>
        </template>
      </TableList>
    </DialogWrapper>
  </div>
</template>

<script>
import _ from 'lodash'
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import HeaderImageUploader from '@/components/uploader/headerImageUploader'
import { mapActions, mapGetters } from 'vuex'
import {
  POST_APPLICANT_DEFAULT_QUESTIONNAIRE_TYPE,
  QUESTIONNAIRE_IMAGE_ACCEPT_FILE_TYPE as ACCEPT_FILE_TYPE,
  QUESTIONNAIRE_IMAGE_ACCEPT_FILE_SIZE as ACCEPT_FILE_SIZE
} from '@/constant/postApplicant'
import { isEn } from '@/i18n'

export default {
  components: {
    TableList,
    WrappedFrom,
    DialogWrapper,
    HeaderImageUploader
  },
  data () {
    return {
      loading: false,
      i18nPrefix: 'admin.company.postApplicant',
      acceptFileType: ACCEPT_FILE_TYPE,
      acceptFileSize: ACCEPT_FILE_SIZE,
      questionnaire: {
        steps: [],
        pc_image: null,
        mobile_image: null,
        type: 'GENERAL',
        micro_site: null
      },
      NPSStepCount: 0,
      selectQuestionnaireStepIds: [],
      questionnaireType: POST_APPLICANT_DEFAULT_QUESTIONNAIRE_TYPE,
      stepsVid: 1,
      showStepSelector: false,
      allStep: [],
      stepList: [],
      stepSearchCondition: {
        type: null,
        keywords: null
      },
      stepSelectorLoading: false,
      microSiteList: [],
      stepHasLoaded: false,
      questionnaireSelectionIds: [],
      formQuestionnaireSelectionIds: []
    }
  },
  computed: {
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    formLines () {
      const lines = {
        micro_site: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.micrositeName`),
          option: {
            data: this.microSiteList,
            valueKey: 'id',
            labelKey: 'name'
          },
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.selectMicrositeName`),
          rules: { required: false, message: this.$t('admin.company.postApplicant.message.questionnaireMicroSiteRequired') }
        },
        name: {
          label: this.$t(`${this.i18nPrefix}.label.questionnaireName`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.questionnaireName`),
          rules: { required: true, message: this.$t(`${this.i18nPrefix}.message.stepNameRequired`) }
        },
        steps: {
          label: this.$t(`${this.i18nPrefix}.label.step`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.step`),
          contentSlot: 'steps',
          rules: { required: true, validator: this.validateSteps, trigger: 'change' }
        }
      }
      return lines
    },
    questionnaireTableColumns () {
      return [
        {
          width: '50px',
          cellClass: 'table-selection',
          columnType: 'selection'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.name.cn`),
          dataKey: 'title_cn',
          width: '110px'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.name.tc`),
          dataKey: 'title_tc',
          width: '110px'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.name.en`),
          dataKey: 'title_en',
          width: '100px'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.stepIcon`),
          dataKey: 'icon',
          slotName: 'icon'
        }
      ]
    },
    formQuestionnaireTableColumns () {
      const formQuestionnaireTableColumns = _.cloneDeep(this.questionnaireTableColumns)
      formQuestionnaireTableColumns.splice(1, 0,
        {
          label: this.$t('common.label.sortOperate'),
          isDragSort: true,
          slotName: 'draggable',
          width: '50px'
        }
      )
      return formQuestionnaireTableColumns
    },
    form () {
      return this.$refs.formEditor
    },
    labelWidth () {
      if (isEn()) {
        return '170px'
      } else {
        return '100px'
      }
    }
  },
  created () {
    this.initData()
    if (this.isEditMode) {
      this.getQuestionnaireData()
    }
    this.initMicroSite()
  },
  mounted () {

  },
  methods: {
    ...mapGetters('account', ['getCurrentAccount']),
    ...mapActions('postApplicantManagement', [
      'getQuestionnaireById',
      'createQuestionnaire',
      'updateQuestionnaire',
      'searchStep',
      'searchMicroSite'
    ]),
    async initData () {
      this.loading = true
      const promiseList = [this.initMicroSite()]
      if (this.isEditMode) {
        promiseList.push(this.getQuestionnaireData())
      }
      try {
        await Promise.all(promiseList)
      } finally {
        this.loading = false
      }
    },
    async getQuestionnaireData () {
      const questionnaireId = this.$route.params.questionnaireId
      const resp = await this.getQuestionnaireById(questionnaireId)
      this.questionnaire = resp
      this.selectQuestionnaireStepIds = _.map(this.questionnaire.steps, item => {
        if (item.contain_nps) {
          this.NPSStepCount++
        }
        return item.id
      })
    },
    async openStepSelector () {
      this.showStepSelector = true
      this.stepSelectorLoading = true
      try {
        if (!this.stepHasLoaded) {
          const resp = await this.searchStep({ status: 1 })
          if (resp) {
            this.stepHasLoaded = true
            this.allStep = resp.steps
          }
        }
        this.fetchStepListData()
      } finally {
        this.stepSelectorLoading = false
      }
    },
    fetchStepListData () {
      this.stepSelectorLoading = true
      const keyword = this.stepSearchCondition.keyword
      this.stepList = _.filter(this.allStep, item => {
        let isKeywords = true
        const isSelected = _.includes(this.selectQuestionnaireStepIds, item.id)
        if (keyword) {
          const searchText = [item.title_cn, item.title_tc, item.title_en]
          isKeywords = _.some(searchText, text => _.includes(text.toLowerCase(), keyword.toLowerCase()))
        }
        return isKeywords && (!isSelected)
      })
      this.stepSelectorLoading = false
    },
    closeStepSelector () {
      this.showStepSelector = false
    },
    handleQuestionnaireSelectionChange (selection) {
      this.questionnaireSelectionIds = _.map(selection, 'id')
    },
    handleFormQuestionnaireSelectionChange (selection) {
      this.formQuestionnaireSelectionIds = _.map(selection, 'id')
    },
    handleStepSelect () {
      const selectStepList = _.chain(this.allStep)
        .filter(item => _.includes(this.questionnaireSelectionIds, item.id))
        .map(item => {
          this.selectQuestionnaireStepIds.push(item.id)
          if (item.contain_nps) {
            this.NPSStepCount++
          }
          return item
        }).value()
      this.questionnaire.steps.push(...selectStepList)
      this.form.clearValidate('steps')
      this.showStepSelector = false
    },
    removeQuestionnaireStep () {
      this.NPSStepCount = 0
      this.questionnaire.steps = _.filter(this.questionnaire.steps, item => !this.formQuestionnaireSelectionIds.includes(item.id))
      this.selectQuestionnaireStepIds = _.map(this.questionnaire.steps, item => {
        if (item.contain_nps) {
          this.NPSStepCount++
        }
        return item.id
      })
      this.formQuestionSelectionIds = []
    },
    validateSteps (rule, value, callback) {
      if (value && value.length > 0 && this.NPSStepCount < 2) {
        callback()
      } else if (this.NPSStepCount > 1) {
        callback(this.$t(`${this.i18nPrefix}.message.NPSStepSingle`))
      } else {
        callback(this.$t(`${this.i18nPrefix}.message.questionnaireStepsRequired`))
      }
    },
    handleCancel () {
      this.$router.push('/v-admin/post-applicant-management/questionnaire/list')
    },
    async handleSave () {
      const uploader = this.$refs.uploader
      let hasImage = false
      if (this.questionnaire.pc_image && this.questionnaire.mobile_image) {
        hasImage = true
      } else if (uploader.newFile.pc && uploader.newFile.mobile) {
        hasImage = true
      }

      if (!hasImage) {
        this.$message.error({
          showClose: true,
          message: this.$t('common.message.pcAndMobileImageRequired')
        })
        return
      }
      let result = false
      result = await this.form.submit()
      if (!result) {
        return
      }
      const questionnaire = this.questionnaire
      _.forEach(questionnaire.steps, (item, index) => {
        item.order = index + 1
      })
      let saveFunction = null
      if (this.isEditMode) {
        saveFunction = this.updateQuestionnaire
      } else {
        saveFunction = this.createQuestionnaire
      }
      this.loading = true
      try {
        try {
          const uploadResult = await uploader.uploadAll(this.generateFileKey)
          if (uploadResult.pc) {
            questionnaire.pc_image = uploadResult.pc
          }
          if (uploadResult.mobile) {
            questionnaire.mobile_image = uploadResult.mobile
          }
        } catch (e) {
          this.$message.error({
            showClose: true,
            message: this.$t('common.message.uploadError')
          })
          return
        }
        const resp = await saveFunction(questionnaire)
        if (resp) {
          this.$router.push({ path: '/v-admin/post-applicant-management/questionnaire/list' })
        }
      } finally {
        this.loading = false
      }
    },
    handleFileChange (file, isPc) {
      const tempPath = window.URL.createObjectURL(file)
      if (isPc) {
        this.questionnaire.pc_image = tempPath
      } else {
        this.questionnaire.mobile_image = tempPath
      }
    },
    generateFileKey (file, isPc) {
      return `postApplicant/image/${isPc ? 'pc' : 'mobile'}/${this.getCurrentAccount().id}/${new Date().valueOf()}`
    },
    async initMicroSite () {
      const resp = await this.searchMicroSite({ status: 1 })
      this.microSiteList = resp.micro_site
    }
  }
}
</script>

<style lang="scss" scoped>
.questionnaire-editor {
  .header {
    border-bottom: 1px solid #e4e7ed;

    .title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }

  .content {
    width: 812px;
    margin: 30px auto 0;

    .steps-list-container {
      .top-btn-group {
        margin-bottom: 20px;
      }
    }

    .bottom-btn-group {
      display: flex;
      justify-content: flex-end;

      .el-button {
        padding: 8px 28px;
        border-radius: 2px;
        line-height: 22px;
        font-size: 16px;
        font-weight: 500;
        color: #0096cc;
      }

      .save-btn {
        margin-left: 24px;
        margin-right: 0;
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }
    }
  }

  .list-page {
    .step-icon {
      font-size: 24px;
    }

    .draggable-handle {
      font-size: 16px;
      cursor: grab;
    }
  }

  .header-image-uploader {
    margin-bottom: 30px;
  }

  .step-selector {
    .filter-container {
      display: flex;
      margin-bottom: 20px;

    }
  }
}
</style>
